import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

/**
 * Higher-Order Component (HOC) to provide React Router v6+ functionality to class components
 * 
 * This HOC wraps class components and injects router-related props:
 * - params: Route parameters (from useParams hook)
 * - navigate: Navigation function (from useNavigate hook)
 * - location: Current location object (from useLocation hook)
 * 
 * Usage:
 * ```
 * class MyComponent extends Component {
 *   componentDidMount() {
 *     const { params, navigate, location } = this.props;
 *     console.log(params.id); // Access route params
 *     // navigate('/path'); // Navigate programmatically
 *     // console.log(location.pathname); // Access location
 *   }
 * }
 * 
 * export default withRouter(MyComponent);
 * ```
 * 
 * For backwards compatibility with React Router v5, this HOC also provides:
 * - match: An object with { params } to match the old API
 * 
 * @param {React.Component} Component - The class component to wrap
 * @returns {React.Component} - Wrapped component with router props injected
 */
function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    return (
      <Component
        {...props}
        params={params}
        navigate={navigate}
        location={location}
        // For backwards compatibility with React Router v5
        match={{ params }}
      />
    );
  }

  // Set display name for better debugging
  ComponentWithRouterProp.displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`;

  return ComponentWithRouterProp;
}

export default withRouter;

